import React, { type ReactElement, useMemo } from 'react';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import {
	useEntryPointButtonTrigger,
	type EntryPointButtonTrigger,
} from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import formatOptionLabelDefault from './format-option-label/index.tsx';
import { FixedWidthPopupComponent } from './popup-container/index.tsx';
import { asyncSelectEntryPoint } from './async-select/entrypoint.tsx';
import type { PickerProps } from './types.tsx';

export type PickerEntryPointProps = Omit<PickerProps, 'onTrigger'> & {
	onTrigger: (arg1: TriggerProps, arg2: EntryPointButtonTrigger) => ReactElement;
};

export const PickerEntryPoint = ({
	isOpen: isPickerOpen,
	value,
	onClose,
	onClearValues,
	onRestoreDefaults,
	id,
	invalidLabel,
	onChange,
	formatOptionLabel = formatOptionLabelDefault,
	onLoadOptions,
	enableFullList,
	operandDisplayValues,
	showOperandDisplayValuesInFullList,
	PopupComponent,
	onTrigger,
	placement = 'bottom-start',
	zIndex,
	CustomNoOptionsMessage,
	pickerComponents,
	fallbackPlacements,
	showOperatorSelect = false,
	onChangeOperator,
	allowedOperators,
	selectedOperator,
	isMulti,
	searchInputPlaceholder,
	shouldRenderToParent = false,
	experienceAnalyticsAttributes,
	isLoading = false,
	CustomFooter,
}: PickerEntryPointProps) => {
	const entryPointParams = useMemo(() => ({}), []);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncSelectEntryPoint,
		entryPointParams,
	);
	const triggerRef = useEntryPointButtonTrigger(entryPointActions);

	return (
		<Box
			role="presentation"
			onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
				if (event.key === 'Escape') {
					// avoid closing a modal on closing a picker with esc key
					isPickerOpen && event.stopPropagation();
					onClose && onClose(event);
				}
			}}
		>
			<Popup
				key={id}
				zIndex={zIndex}
				placement={placement}
				isOpen={isPickerOpen}
				onClose={onClose}
				popupComponent={PopupComponent ?? FixedWidthPopupComponent}
				trigger={(triggerProps) => onTrigger(triggerProps, triggerRef)}
				autoFocus={false}
				fallbackPlacements={fallbackPlacements}
				testId={`jql-builder-basic-picker-${id}`}
				shouldRenderToParent={shouldRenderToParent}
				/* Our default popup component supports resizing to fit the viewport. */
				shouldFitViewport={PopupComponent === undefined}
				content={({ setInitialFocusRef }) => (
					<JiraEntryPointContainer
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="jql-builder-basic-picker"
						packageName="@atlassian/jira-jql-builder-basic-picker"
						errorFallback="flag"
						runtimeProps={{
							id,
							invalidLabel,
							enableFullList,
							operandDisplayValues,
							showOperandDisplayValuesInFullList,
							loadOptions: onLoadOptions,
							onChange,
							value,
							formatOptionLabel,
							onClear: onClearValues,
							onRestoreDefaults,
							NoOptionsMessage: CustomNoOptionsMessage,
							components: pickerComponents,
							showOperatorSelect,
							onChangeOperator,
							allowedOperators,
							selectedOperator,
							isMulti,
							placeholder: searchInputPlaceholder,
							experienceAnalyticsAttributes,
							isLoading,
							CustomFooter: fg('jira_inline_field_config_gate') ? CustomFooter : undefined,
							setInitialFocusRef,
						}}
					/>
				)}
			/>
		</Box>
	);
};
